/* eslint-disable no-unused-vars */
// RescheduleInterviewScreen_FullyFunctional.js
// Fully functional and productive interview rescheduling screen
// Complete business functionality for rescheduling interviews with real-time availability

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  TextInput,
  Alert,
  Dimensions,
  KeyboardAvoidingView,
  Platform,
  Modal,
  FlatList,
  ActivityIndicator,
  Switch,
  StatusBar,
} from 'react-native';
import {
  Ionicons,
  MaterialCommunityIcons,
  MaterialIcons,
  AntDesign,
  FontAwesome5,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

const RescheduleInterviewScreen = ({ route, navigation }) => {
  // Get interview data from route params
  const interview = route?.params?.interview || null;
  const onInterviewRescheduled = route?.params?.onInterviewRescheduled || (() => {});

  // State management
  const [formData, setFormData] = useState({
    date: '',
    time: '',
    location: '',
    interviewers: [],
    interviewType: '',
    duration: 60,
    reason: '',
    notifyParticipants: true,
    notes: '',
    priority: 'medium',
    meetingLink: '',
    preparationTime: 15,
    bufferTime: 15,
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [showInterviewerPicker, setShowInterviewerPicker] = useState(false);
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [interviewTypes, setInterviewTypes] = useState([]);
  const [originalInterview, setOriginalInterview] = useState(null);


  // ── Company logo loader (auto-injected) ─────────────────────────────────────
  // Hydrates from cache for instant paint, then refreshes from
  // /api/company-profile/:id so any logo / name change made elsewhere
  // (Settings, Dashboard, etc.) propagates to this screen on next open.
  const [_companyLogo,  _setCompanyLogo]  = useState(null);
  const [_companyName,  _setCompanyName]  = useState('');
  useEffect(() => { _loadCompanyLogo(); }, []);
  const _loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        _setCompanyLogo(profile.logo || null);
        if (profile.name || profile.company_name) _setCompanyName(profile.name || profile.company_name);
      }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        try {
          const res = await fetch(`https://api.jobeng.ai/api/company-profile/${cid}`, {
            headers: { 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' }
          });
          if (res.ok) {
            const json = await res.json();
            const row = (json?.data && !Array.isArray(json.data)) ? json.data
                     : (Array.isArray(json?.data) ? json.data[0] : json);
            if (row && (row.logo || row.name || row.company_name)) {
              if (row.logo) _setCompanyLogo(row.logo);
              const nm = row.name || row.company_name;
              if (nm) _setCompanyName(nm);
              await AsyncStorage.setItem('company_profile', JSON.stringify(row));
              console.log('[CompanyLogo] refreshed from API for cid=' + cid);
            }
          }
        } catch (e) { /* offline — cache is enough */ }
      }
    } catch (e) { console.warn('Logo load error:', e.message); }
  };
  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  // Initialize form data when interview is loaded
  useEffect(() => {
    if (interview) {
      initializeFormData();
    }
  }, [interview]);

  // Check availability when date or interviewers change. The check now runs
  // even if no interviewers are selected — we still need to surface the
  // standard business-hour slots so the time picker has options.
  useEffect(() => {
    if (formData.date) {
      checkAvailability();
    } else {
      setAvailableTimeSlots([]);
      setConflicts([]);
    }
  }, [formData.date, formData.interviewers]);

  // Load all necessary data
  const loadData = async () => {
    try {
      setIsLoading(true);

      // Interviewers — load real company employees from /api/data/employees.
      // The previous version queried 'interviewers' which isn't a real table,
      // silently failed, and substituted mock people. Now we read the actual
      // team. If employees table is empty we still surface the current user
      // so the picker isn't empty.
      try {
        const companyId = parseInt(await AsyncStorage.getItem('company_id') || '0', 10);
        if (companyId) {
          const token = await AsyncStorage.getItem('auth_token').catch(() => null);
          const res = await fetch(`${API_BASE_URL}/api/data/employees?company_id=${companyId}&limit=100`, {
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          });
          let rows = [];
          if (res.ok) {
            const body = await res.json();
            rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
          }
          const mapped = rows.map(e => ({
            id: String(e.id),
            name: e.full_name || e.name || `${e.first_name || ''} ${e.last_name || ''}`.trim() || e.email || 'Team member',
            role: e.job_title || e.position || e.title || '',
            email: e.email || '',
            avatar: e.avatar_url || e.profile_image || null,
            available: true,
          }));
          // Fall back to current user if no employees on file
          if (mapped.length === 0) {
            const cached = await AsyncStorage.getItem('user_profile').catch(() => null);
            if (cached) {
              try {
                const u = JSON.parse(cached);
                mapped.push({
                  id: String(u.id || 'me'),
                  name: u.full_name || u.name || 'You',
                  role: u.job_title || 'Recruiter',
                  email: u.email || '',
                  avatar: u.avatar_url || null,
                  available: true,
                });
              } catch (_) {}
            }
          }
          setInterviewers(mapped);
          await AsyncStorage.setItem('interviewers', JSON.stringify(mapped)).catch(() => {});
        }
      } catch (e) {
        console.warn('loadInterviewers:', e?.message);
        // Offline cache fallback only
        try {
          const stored = await AsyncStorage.getItem('interviewers');
          if (stored) setInterviewers(JSON.parse(stored));
          else setInterviewers([]);
        } catch { setInterviewers([]); }
      }

      // Locations + interview types — these are UI enums, not DB tables.
      // The old code POSTed defaults to non-existent tables on every load.
      // Now they're just local constants the picker displays.
      setLocations([
        { id: 'video',     name: 'Video Call',       icon: 'videocam' },
        { id: 'phone',     name: 'Phone Call',       icon: 'phone' },
        { id: 'in_person', name: 'In Person',        icon: 'office-building' },
        { id: 'office',    name: 'Company Office',   icon: 'office-building' },
        { id: 'hybrid',    name: 'Hybrid',           icon: 'home-group' },
      ]);
      setInterviewTypes([
        { id: 'technical',   name: 'Technical',          duration: 90 },
        { id: 'behavioral',  name: 'Behavioral',         duration: 60 },
        { id: 'cultural',    name: 'Cultural Fit',       duration: 45 },
        { id: 'system_design', name: 'System Design',    duration: 90 },
        { id: 'coding',      name: 'Live Coding',        duration: 60 },
        { id: 'final',       name: 'Final Round',        duration: 60 },
        { id: 'phone_screen',name: 'Phone Screen',       duration: 30 },
        { id: 'panel',       name: 'Panel Interview',    duration: 75 },
      ]);

    } catch (error) {
      console.error('Error loading data:', error);
      Alert.alert('Error', 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate default interviewers with realistic availability
  const generateDefaultInterviewers = () => [
    {
      id: '1',
      name: 'Dr. Sarah Johnson',
      title: 'Senior Technical Lead',
      department: 'Engineering',
      email: 'sarah.johnson@company.com',
      phone: '+44 7700 900123',
      expertise: ['JavaScript', 'React', 'Node.js', 'System Design'],
      availability: generateAvailabilitySchedule(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 4,
      rating: 4.8,
      totalInterviews: 156,
    },
    {
      id: '2',
      name: 'Michael Chen',
      title: 'Product Manager',
      department: 'Product',
      email: 'michael.chen@company.com',
      phone: '+44 7700 900124',
      expertise: ['Product Strategy', 'Analytics', 'User Research', 'Leadership'],
      availability: generateAvailabilitySchedule(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 3,
      rating: 4.9,
      totalInterviews: 89,
    },
    {
      id: '3',
      name: 'Emma Wilson',
      title: 'HR Business Partner',
      department: 'Human Resources',
      email: 'emma.wilson@company.com',
      phone: '+44 7700 900125',
      expertise: ['Behavioral Assessment', 'Cultural Fit', 'Compensation', 'Benefits'],
      availability: generateAvailabilitySchedule(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person', 'Phone'],
      maxInterviewsPerDay: 6,
      rating: 4.7,
      totalInterviews: 234,
    },
    {
      id: '4',
      name: 'James Rodriguez',
      title: 'Engineering Manager',
      department: 'Engineering',
      email: 'james.rodriguez@company.com',
      phone: '+44 7700 900126',
      expertise: ['Team Leadership', 'Architecture', 'Code Review', 'Mentoring'],
      availability: generateAvailabilitySchedule(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 3,
      rating: 4.9,
      totalInterviews: 178,
    },
    {
      id: '5',
      name: 'Lisa Martinez',
      title: 'UX Design Lead',
      department: 'Design',
      email: 'lisa.martinez@company.com',
      phone: '+44 7700 900127',
      expertise: ['User Experience', 'Design Systems', 'Prototyping', 'User Research'],
      availability: generateAvailabilitySchedule(),
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 4,
      rating: 4.8,
      totalInterviews: 92,
    },
    {
      id: '6',
      name: 'David Kim',
      title: 'CTO',
      department: 'Executive',
      email: 'david.kim@company.com',
      phone: '+44 7700 900128',
      expertise: ['Technology Strategy', 'Leadership', 'Innovation', 'Scaling'],
      availability: generateLimitedAvailabilitySchedule(), // CTOs have limited availability
      timezone: 'GMT',
      preferredMeetingTypes: ['Video', 'In-person'],
      maxInterviewsPerDay: 2,
      rating: 5.0,
      totalInterviews: 45,
    },
  ];

  // Generate availability schedule for next 30 days
  const generateAvailabilitySchedule = () => {
    const schedule = {};
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip weekends for most availability
      if (date.getDay() === 0 || date.getDay() === 6) {
        schedule[dateStr] = [];
        continue;
      }
      
      // Generate random availability (more realistic)
      const slots = [];
      const possibleSlots = [
        '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
        '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30'
      ];
      
      // Randomly select 60-80% of slots as available
      possibleSlots.forEach(slot => {
        if (Math.random() > 0.3) { // 70% chance of being available
          slots.push(slot);
        }
      });
      
      schedule[dateStr] = slots;
    }
    
    return schedule;
  };

  // Generate limited availability for executives
  const generateLimitedAvailabilitySchedule = () => {
    const schedule = {};
    const today = new Date();
    
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      const dateStr = date.toISOString().split('T')[0];
      
      // Skip weekends
      if (date.getDay() === 0 || date.getDay() === 6) {
        schedule[dateStr] = [];
        continue;
      }
      
      // Limited availability for executives
      const slots = [];
      const possibleSlots = ['10:00', '11:00', '14:00', '15:00', '16:00'];
      
      // Only 40% chance of being available
      possibleSlots.forEach(slot => {
        if (Math.random() > 0.6) {
          slots.push(slot);
        }
      });
      
      schedule[dateStr] = slots;
    }
    
    return schedule;
  };

  // Generate default locations
  const generateDefaultLocations = () => [
    {
      id: '1',
      name: 'Conference Room A',
      type: 'Physical',
      capacity: 8,
      floor: '2nd Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Whiteboard', 'Projector', 'Video Conference', 'Coffee'],
      bookingRequired: true,
      available: true,
    },
    {
      id: '2',
      name: 'Executive Boardroom',
      type: 'Physical',
      capacity: 12,
      floor: '5th Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Large Screen', 'Video Conference', 'Catering', 'Premium Seating'],
      bookingRequired: true,
      available: true,
    },
    {
      id: '3',
      name: 'Meeting Room B',
      type: 'Physical',
      capacity: 4,
      floor: '3rd Floor',
      building: 'Main Office',
      address: '123 Business Street, London, UK',
      amenities: ['Whiteboard', 'Video Conference'],
      bookingRequired: true,
      available: true,
    },
    {
      id: '4',
      name: 'Zoom Video Conference',
      type: 'Virtual',
      capacity: 100,
      platform: 'Zoom',
      features: ['Screen Sharing', 'Recording', 'Breakout Rooms', 'Chat'],
      meetingLink: 'https://zoom.us/j/meeting-room',
      dialIn: '+44 203 481 5237',
      available: true,
    },
    {
      id: '5',
      name: 'Microsoft Teams',
      type: 'Virtual',
      capacity: 250,
      platform: 'Teams',
      features: ['Screen Sharing', 'Recording', 'File Sharing', 'Chat'],
      meetingLink: 'https://teams.microsoft.com/l/meetup-join',
      available: true,
    },
    {
      id: '6',
      name: 'Google Meet',
      type: 'Virtual',
      capacity: 100,
      platform: 'Google Meet',
      features: ['Screen Sharing', 'Recording', 'Live Captions'],
      meetingLink: 'https://meet.google.com/meeting-room',
      available: true,
    },
    {
      id: '7',
      name: 'Phone Interview',
      type: 'Phone',
      capacity: 10,
      description: 'Traditional phone interview',
      available: true,
    },
  ];

  // Generate default interview types
  const generateDefaultInterviewTypes = () => [
    {
      id: '1',
      name: 'Initial Screening',
      duration: 30,
      color: '#3B82F6',
      description: 'Initial phone/video screening with HR',
      stages: ['Introduction', 'Basic Questions', 'Next Steps'],
      requiredInterviewers: ['HR'],
      preparationTime: 10,
      followUpRequired: true,
    },
    {
      id: '2',
      name: 'Technical Interview',
      duration: 90,
      color: '#10B981',
      description: 'Technical assessment and coding interview',
      stages: ['Technical Questions', 'Coding Challenge', 'System Design'],
      requiredInterviewers: ['Technical Lead', 'Senior Developer'],
      preparationTime: 15,
      followUpRequired: true,
    },
    {
      id: '3',
      name: 'Behavioral Interview',
      duration: 60,
      color: '#F59E0B',
      description: 'Behavioral and cultural fit assessment',
      stages: ['Behavioral Questions', 'Scenario Discussion', 'Culture Fit'],
      requiredInterviewers: ['HR', 'Hiring Manager'],
      preparationTime: 10,
      followUpRequired: true,
    },
    {
      id: '4',
      name: 'Panel Interview',
      duration: 90,
      color: '#8B5CF6',
      description: 'Multi-interviewer panel discussion',
      stages: ['Introductions', 'Technical Discussion', 'Q&A Session'],
      requiredInterviewers: ['Multiple'],
      preparationTime: 20,
      followUpRequired: true,
    },
    {
      id: '5',
      name: 'Final Interview',
      duration: 60,
      color: '#EF4444',
      description: 'Final interview with senior leadership',
      stages: ['Leadership Discussion', 'Company Vision', 'Offer Discussion'],
      requiredInterviewers: ['Senior Leadership'],
      preparationTime: 15,
      followUpRequired: true,
    },
    {
      id: '6',
      name: 'Portfolio Review',
      duration: 45,
      color: '#06B6D4',
      description: 'Review of candidate portfolio and work samples',
      stages: ['Portfolio Presentation', 'Work Discussion', 'Feedback'],
      requiredInterviewers: ['Design Lead', 'Creative Director'],
      preparationTime: 10,
      followUpRequired: false,
    },
  ];

  // Initialize form data from existing interview
  const initializeFormData = () => {
    if (!interview) return;

    try {
      // The interview row may come from /api/recruitment/applicant/:id (real
      // DB JOIN) OR from a list cached locally. Either way pull from the
      // canonical column names first, falling back to legacy camelCase ones.
      const scheduled = interview.scheduled_date || interview.scheduledAt || interview.interviewDate || interview.date;
      const interviewDate = scheduled ? new Date(scheduled) : new Date();
      const formattedDate = interviewDate.toISOString().slice(0, 10);
      const formattedTime = `${String(interviewDate.getHours()).padStart(2, '0')}:${String(interviewDate.getMinutes()).padStart(2, '0')}`;

      // Resolve interviewer ids — the row stores either a JSONB array of
      // {id, name, role} (what ScheduleInterview writes) or a string array
      // of names (legacy). Map back to local interviewer ids by name.
      let interviewerIds = [];
      if (Array.isArray(interview.interviewers)) {
        interviewerIds = interview.interviewers
          .map(item => {
            if (item && typeof item === 'object') {
              // Match by id first, then by name
              const byId = interviewers.find(i => String(i.id) === String(item.id));
              if (byId) return byId.id;
              const byName = interviewers.find(i => i.name === item.name);
              return byName?.id;
            }
            const byName = interviewers.find(i => i.name === item);
            return byName?.id;
          })
          .filter(Boolean);
      }
      // If the row only has an interviewer_name (no panel), try to resolve it
      if (interviewerIds.length === 0 && interview.interviewer_name) {
        const found = interviewers.find(i => i.name === interview.interviewer_name);
        if (found) interviewerIds = [found.id];
      }

      // Location can be a stored string ("Video Call") or our local id ("video")
      const locName = interview.location || interview.interviewLocation || '';
      const locMatch = locations.find(l => l.id === locName || l.name === locName);
      const locId = locMatch ? locMatch.id : (locName ? 'video' : '');

      // Same for interview type
      const typeName = interview.interview_type || interview.interviewStage || interview.type || '';
      const typeMatch = interviewTypes.find(t => t.id === typeName || String(t.name ?? '').toLowerCase() === String(typeName).toLowerCase());
      const typeId = typeMatch ? typeMatch.id : (typeName || '');

      setFormData({
        date: formattedDate,
        time: formattedTime,
        location: locId,
        interviewers: interviewerIds,
        interviewType: typeId,
        duration: interview.duration_minutes || interview.duration || 60,
        reason: '',
        notifyParticipants: true,
        notes: interview.notes || interview.instructions || '',
        priority: interview.priority || 'medium',
        meetingLink: interview.meeting_link || interview.meetingLink || '',
        preparationTime: interview.preparationTime || 15,
        bufferTime: interview.bufferTime || 15,
      });

      setOriginalInterview(interview);
    } catch (error) {
      console.error('Error initializing form data:', error);
      Alert.alert('Error', 'Failed to load interview data');
    }
  };

  // Check availability by querying real interview conflicts from the DB.
  // The previous version checked `interviewer.availability[date]` — a property
  // that only existed on mock data. Real employees from /api/data/employees
  // don't have it, so commonSlots always collapsed to [] and validateForm
  // then rejected every save with "time not available for all interviewers".
  //
  // Now we treat all business-hour slots as available by default, then mark
  // any slots that already have an interview booked (for this same date and
  // any of the selected interviewers) as conflicts.
  const checkAvailability = async () => {
    const selectedDate = formData.date;
    const selectedInterviewers = formData.interviewers;

    if (!selectedDate) {
      setAvailableTimeSlots([]);
      setConflicts([]);
      return;
    }

    // Standard business-hour slots — always offer these
    const baseSlots = [
      '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
      '13:00', '13:30', '14:00', '14:30', '15:00', '15:30', '16:00', '16:30',
    ];

    // Query existing interviews on this date for our selected interviewers,
    // so we can grey out conflicting slots. Non-blocking — if the call fails
    // we still offer all slots.
    let bookedSlots = new Set();
    try {
      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const res = await fetch(
        `${API_BASE_URL}/api/data/application_interviews?status=scheduled&limit=200`,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );
      if (res.ok) {
        const body = await res.json();
        const rows = Array.isArray(body) ? body : (Array.isArray(body?.data) ? body.data : []);
        // Each row's scheduled_date is a full timestamp; extract date + HH:MM
        rows.forEach(r => {
          if (!r.scheduled_date) return;
          // Skip the row we're rescheduling (no conflict with ourselves)
          if (interview?.id && String(r.id) === String(interview.id)) return;
          const dt = new Date(r.scheduled_date);
          const isoDate = dt.toISOString().slice(0, 10);
          if (isoDate !== selectedDate) return;
          // Only count interviews whose interviewer matches one of ours
          const rowInterviewers = Array.isArray(r.interviewers) ? r.interviewers : [];
          const overlaps =
            selectedInterviewers.length === 0 ||
            rowInterviewers.some(p =>
              selectedInterviewers.some(myId =>
                String(p.id) === String(myId) ||
                (p.name && interviewers.find(i =>
                  String(i.id) === String(myId) && i.name === p.name)))
            );
          if (overlaps) {
            const hhmm = `${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
            bookedSlots.add(hhmm);
          }
        });
      }
    } catch (_) { /* non-fatal — fall through to all-available */ }

    const commonSlots = baseSlots.filter(s => !bookedSlots.has(s));
    setAvailableTimeSlots(commonSlots);

    // Surface advisories (not blockers)
    const newConflicts = [];
    if (bookedSlots.size > 0) {
      newConflicts.push({
        type: 'availability',
        message: `${bookedSlots.size} time slot${bookedSlots.size === 1 ? '' : 's'} already booked on this date`,
        severity: 'low',
      });
    }
    // Weekend warning (not blocking)
    const selectedDateObj = new Date(selectedDate);
    if (selectedDateObj.getDay() === 0 || selectedDateObj.getDay() === 6) {
      newConflicts.push({
        type: 'weekend',
        message: 'Interview scheduled on weekend',
        severity: 'medium',
      });
    }
    // Short notice warning (not blocking)
    const now = new Date();
    const timeDiff = selectedDateObj.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);
    if (hoursDiff < 24 && hoursDiff > 0) {
      newConflicts.push({
        type: 'short_notice',
        message: 'Less than 24 hours notice',
        severity: 'medium',
      });
    }
    setConflicts(newConflicts);
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    // Date validation
    if (!formData.date) {
      newErrors.date = 'Interview date is required';
    } else {
      const selectedDate = new Date(formData.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.date = 'Interview date cannot be in the past';
      }
    }

    // Time validation — only require a value. Availability conflicts
    // surface as advisories in the conflicts strip, not as hard blockers.
    // This lets the recruiter override a "booked" slot if they know what
    // they're doing (e.g. rescheduling the very interview that's "blocking").
    if (!formData.time) {
      newErrors.time = 'Interview time is required';
    }

    // Location validation
    if (!formData.location) {
      newErrors.location = 'Interview location is required';
    }

    // Interviewers validation
    if (formData.interviewers.length === 0) {
      newErrors.interviewers = 'At least one interviewer must be selected';
    }

    // Interview type validation
    if (!formData.interviewType) {
      newErrors.interviewType = 'Interview type is required';
    }

    // Reason validation
    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason for rescheduling is required';
    } else if (formData.reason.trim().length < 10) {
      newErrors.reason = 'Please provide a more detailed reason (minimum 10 characters)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  // Handle interviewer selection
  const handleInterviewerToggle = (interviewerId) => {
    const currentInterviewers = formData.interviewers;
    const newInterviewers = currentInterviewers.includes(interviewerId)
      ? currentInterviewers.filter(id => id !== interviewerId)
      : [...currentInterviewers, interviewerId];

    handleInputChange('interviewers', newInterviewers);
  };

  // Handle save with validation and conflict checking
  const handleSave = async () => {
    if (!validateForm()) {
      Alert.alert(
        'Validation Error',
        'Please correct the errors before saving.',
        [{ text: 'OK' }]
      );
      return;
    }

    // Check for high severity conflicts
    const highSeverityConflicts = conflicts.filter(c => c.severity === 'high');
    if (highSeverityConflicts.length > 0) {
      setShowConflictModal(true);
      return;
    }

    // Check for medium severity conflicts
    const mediumSeverityConflicts = conflicts.filter(c => c.severity === 'medium');
    if (mediumSeverityConflicts.length > 0) {
      Alert.alert(
        'Scheduling Conflicts',
        mediumSeverityConflicts.map(c => c.message).join('\n\n'),
        [
          { text: 'Cancel', style: 'cancel' },
          { text: 'Proceed Anyway', onPress: () => performReschedule() },
        ]
      );
      return;
    }

    performReschedule();
  };

  // Perform the actual rescheduling
  // Writes to the canonical application_interviews row — the SAME row the
  // candidate's VideoInterviewScreen reads from. PUTs the new scheduled_date,
  // status=rescheduled, and the recruiter's notes/reason. Both sides see the
  // change immediately. The previous version tried PUT 'interviews' with an
  // array body (wrong shape, wrong table) and only persisted to AsyncStorage.
  const performReschedule = async () => {
    setIsSaving(true);

    try {
      // Resolve the interview row id. If the screen was opened with a route
      // param interview, that row's id is what we update. If not, we treat
      // this as a new interview and POST instead.
      const ivId = interview?.id || originalInterview?.id || null;
      const applicationId =
        interview?.application_id ||
        interview?.applicationId ||
        originalInterview?.application_id ||
        route?.params?.applicantId ||
        route?.params?.applicant?.id ||
        null;

      // Compose scheduled_date timestamp from form date + time
      let scheduledAt;
      try {
        scheduledAt = new Date(`${String(formData.date).slice(0, 10)}T${String(formData.time).slice(0, 5)}:00`).toISOString();
      } catch { scheduledAt = new Date().toISOString(); }

      // Get the panel of interviewer names + ids (matches what ScheduleInterview writes)
      const panel = formData.interviewers.map(id => {
        const intv = interviewers.find(i => String(i.id) === String(id));
        return intv ? { id: intv.id, name: intv.name, role: intv.role || '' } : null;
      }).filter(Boolean);

      const locDetails = locations.find(l => l.id === formData.location);
      const typeDetails = interviewTypes.find(t => t.id === formData.interviewType);

      // Payload matching the application_interviews schema
      const payload = {
        scheduled_date:   scheduledAt,
        duration_minutes: Number(formData.duration) || 60,
        location:         locDetails ? locDetails.name : (formData.location || null),
        meeting_link:     formData.meetingLink || null,
        interview_type:   typeDetails ? typeDetails.id : (formData.interviewType || 'video'),
        interviewer_name: panel[0]?.name || null,
        // interviewers is JSONB — pre-stringify or PG returns "invalid input
        // syntax for type json" via the generic /api/data/:table handler.
        interviewers:     panel.length ? JSON.stringify(panel) : null,
        instructions:     formData.notes || null,
        status:           'rescheduled',
        notes:            formData.reason
          ? `Rescheduled: ${formData.reason}${formData.notes ? `\n\n${formData.notes}` : ''}`
          : (formData.notes || null),
        updated_at:       new Date().toISOString(),
      };
      Object.keys(payload).forEach(k => payload[k] == null && delete payload[k]);

      const token = await AsyncStorage.getItem('auth_token').catch(() => null);
      const headers = {
        'Content-Type': 'application/json',
        'x-api-key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      let savedRow = null;
      if (ivId && /^\d+$/.test(String(ivId))) {
        // Update existing interview row
        const res = await fetch(`${API_BASE_URL}/api/data/application_interviews/${ivId}`, {
          method: 'PUT', headers, body: JSON.stringify(payload),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Reschedule failed: ${res.status} ${text.slice(0, 200)}`);
        }
        savedRow = await res.json().catch(() => ({}));
      } else if (applicationId) {
        // No existing interview — POST a fresh one. Treat the reschedule as
        // a brand-new interview tied to this application.
        const fullPayload = { ...payload, application_id: parseInt(applicationId, 10), status: 'scheduled' };
        const res = await fetch(`${API_BASE_URL}/api/data/application_interviews`, {
          method: 'POST', headers, body: JSON.stringify(fullPayload),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`Create failed: ${res.status} ${text.slice(0, 200)}`);
        }
        savedRow = await res.json().catch(() => ({}));
      } else {
        throw new Error('No interview id and no applicant — cannot reschedule.');
      }

      // Add a timeline entry so the change shows up in the applicant's history
      if (applicationId) {
        try {
          await fetch(`${API_BASE_URL}/api/recruitment/applicant/${applicationId}/stage`, {
            method: 'POST', headers,
            body: JSON.stringify({
              stage: 'interviewed',
              note: formData.reason
                ? `Interview rescheduled: ${formData.reason}`
                : 'Interview rescheduled',
            }),
          });
        } catch (_) { /* non-fatal */ }
      }

      // Mirror locally for offline reads (UI-friendly shape)
      const localShape = {
        ...originalInterview,
        ...interview,
        id: savedRow?.id || ivId || `interview_${Date.now()}`,
        scheduled_date: scheduledAt,
        scheduledAt,
        date: scheduledAt,
        location: payload.location,
        interviewers: panel.map(p => p.name),
        type: payload.interview_type,
        status: payload.status,
        duration_minutes: payload.duration_minutes,
        duration: payload.duration_minutes,
        meeting_link: payload.meeting_link,
        meetingLink: payload.meeting_link,
        notes: payload.notes,
        rescheduleReason: formData.reason,
        rescheduleDate: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        version: (originalInterview?.version || 0) + 1,
      };
      try {
        const stored = await AsyncStorage.getItem('allInterviews');
        const list = stored ? JSON.parse(stored) : [];
        const idx = list.findIndex(i => String(i.id) === String(localShape.id));
        if (idx >= 0) list[idx] = localShape; else list.push(localShape);
        await AsyncStorage.setItem('allInterviews', JSON.stringify(list));
      } catch (_) {}

      // Fire the parent callback so the originating screen can refresh
      onInterviewRescheduled(localShape);

      // Show success message
      const rescheduledDate = new Date(scheduledAt);
      const dateStr = rescheduledDate.toLocaleDateString('en-GB', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
      });
      const timeStr = rescheduledDate.toLocaleTimeString('en-GB', {
        hour: '2-digit', minute: '2-digit',
      });

      const successMessage = `Interview rescheduled for ${dateStr} at ${timeStr}.${
        formData.notifyParticipants
          ? '\n\nAll participants will see the new time on their dashboards.'
          : '\n\nRemember to manually notify all participants.'
      }`;

      Alert.alert(
        'Interview Rescheduled',
        successMessage,
        [{ text: 'OK', onPress: () => navigation.goBack() }]
      );

    } catch (error) {
      console.error('Error performing reschedule:', error);
      Alert.alert(
        'Error',
        'Failed to reschedule interview. Please check your connection and try again.',
        [{ text: 'OK' }]
      );
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel with confirmation
  const handleCancel = () => {
    // Check if form has been modified
    const hasChanges = originalInterview && (
      formData.date !== originalInterview.interviewDate?.split('T')[0] ||
      formData.time !== `${String(new Date(originalInterview.interviewDate).getHours()).padStart(2, '0')}:${String(new Date(originalInterview.interviewDate).getMinutes()).padStart(2, '0')}` ||
      formData.reason.trim() !== ''
    );

    if (hasChanges) {
      Alert.alert(
        'Discard Changes',
        'You have unsaved changes. Are you sure you want to discard them?',
        [
          { text: 'Keep Editing', style: 'cancel' },
          {
            text: 'Discard',
            style: 'destructive',
            onPress: () => navigation.goBack(),
          },
        ]
      );
    } else {
      navigation.goBack();
    }
  };

  // Generate next available dates
  const generateNextDays = (count = 21) => {
    const dates = [];
    const today = new Date();

    for (let i = 0; i < count; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }

    return dates;
  };

  // Render date picker modal
  const renderDatePicker = () => {
    const dates = generateNextDays();

    return (
      <Modal visible={showDatePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Date</Text>
              <TouchableOpacity onPress={() => setShowDatePicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={dates}
              keyExtractor={(item, index) => index.toString()}
              style={styles.modalContent}
              renderItem={({ item: date }) => {
                const dateFormatted = date.toISOString().split('T')[0];
                const isToday = dateFormatted === new Date().toISOString().split('T')[0];
                const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                const dateDisplay = date.toLocaleDateString('en-GB', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                });

                return (
                  <TouchableOpacity
                    style={[
                      styles.modalOption,
                      isWeekend && styles.weekendOption,
                      formData.date === dateFormatted && styles.selectedOption,
                    ]}
                    onPress={() => {
                      handleInputChange('date', dateFormatted);
                      setShowDatePicker(false);
                    }}
                  >
                    <View style={styles.dateOptionContent}>
                      <Text style={[
                        styles.modalOptionText,
                        isWeekend && styles.weekendText,
                        formData.date === dateFormatted && styles.selectedOptionText,
                      ]}>
                        {dateDisplay}
                      </Text>
                      {isToday && (
                        <View style={styles.todayBadge}>
                          <Text style={styles.todayBadgeText}>Today</Text>
                        </View>
                      )}
                      {isWeekend && (
                        <MaterialCommunityIcons name="calendar-weekend" size={16} color="#F59E0B" />
                      )}
                    </View>
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render time picker modal
  const renderTimePicker = () => {
    return (
      <Modal visible={showTimePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Time</Text>
              <TouchableOpacity onPress={() => setShowTimePicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <View style={styles.modalContent}>
              {availableTimeSlots.length > 0 ? (
                <FlatList
                  data={availableTimeSlots}
                  keyExtractor={(item) => item}
                  numColumns={3}
                  renderItem={({ item: time }) => (
                    <TouchableOpacity
                      style={[
                        styles.timeSlot,
                        formData.time === time && styles.selectedTimeSlot,
                      ]}
                      onPress={() => {
                        handleInputChange('time', time);
                        setShowTimePicker(false);
                      }}
                    >
                      <Text style={[
                        styles.timeSlotText,
                        formData.time === time && styles.selectedTimeSlotText,
                      ]}>
                        {time}
                      </Text>
                    </TouchableOpacity>
                  )}
                />
              ) : (
                <View style={styles.noAvailabilityContainer}>
                  <MaterialCommunityIcons name="calendar-remove" size={48} color="#CBD5E1" />
                  <Text style={styles.noAvailabilityTitle}>No Available Times</Text>
                  <Text style={styles.noAvailabilityText}>
                    No common time slots available for selected date and interviewers.
                    Please select a different date or different interviewers.
                  </Text>
                </View>
              )}
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // Render location picker modal
  const renderLocationPicker = () => {
    return (
      <Modal visible={showLocationPicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Location</Text>
              <TouchableOpacity onPress={() => setShowLocationPicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={locations}
              keyExtractor={(item) => item.id}
              style={styles.modalContent}
              renderItem={({ item: location }) => (
                <TouchableOpacity
                  style={[
                    styles.locationOption,
                    formData.location === location.id && styles.selectedLocationOption,
                  ]}
                  onPress={() => {
                    handleInputChange('location', location.id);
                    if (location.type === 'Virtual' && location.meetingLink) {
                      handleInputChange('meetingLink', location.meetingLink);
                    }
                    setShowLocationPicker(false);
                  }}
                >
                  <View style={styles.locationHeader}>
                    <View style={styles.locationInfo}>
                      <Text style={[
                        styles.locationName,
                        formData.location === location.id && styles.selectedLocationName,
                      ]}>
                        {location.name}
                      </Text>
                      <View style={styles.locationMeta}>
                        <View style={[
                          styles.locationTypeBadge,
                          { backgroundColor: getLocationTypeColor(location.type) }
                        ]}>
                          <Text style={styles.locationTypeBadgeText}>{location.type}</Text>
                        </View>
                        <Text style={styles.locationCapacity}>
                          Capacity: {location.capacity}
                        </Text>
                      </View>
                    </View>
                    {formData.location === location.id && (
                      <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
                    )}
                  </View>
                  
                  {location.type === 'Physical' && (
                    <View style={styles.locationDetails}>
                      <Text style={styles.locationAddress}>
                        {location.floor && `${location.floor}, `}
                        {location.building && `${location.building}`}
                      </Text>
                      {location.amenities && (
                        <View style={styles.amenitiesContainer}>
                          {location.amenities.slice(0, 3).map((amenity, index) => (
                            <View key={index} style={styles.amenityChip}>
                              <Text style={styles.amenityText}>{amenity}</Text>
                            </View>
                          ))}
                          {location.amenities.length > 3 && (
                            <Text style={styles.moreAmenities}>
                              +{location.amenities.length - 3} more
                            </Text>
                          )}
                        </View>
                      )}
                    </View>
                  )}
                  
                  {location.type === 'Virtual' && (
                    <View style={styles.locationDetails}>
                      <Text style={styles.platformText}>Platform: {location.platform}</Text>
                      {location.features && (
                        <View style={styles.featuresContainer}>
                          {location.features.slice(0, 3).map((feature, index) => (
                            <View key={index} style={styles.featureChip}>
                              <Text style={styles.featureText}>{feature}</Text>
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render interviewer picker modal
  const renderInterviewerPicker = () => {
    return (
      <Modal visible={showInterviewerPicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interviewers</Text>
              <TouchableOpacity onPress={() => setShowInterviewerPicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={interviewers}
              keyExtractor={(item) => item.id}
              style={styles.modalContent}
              renderItem={({ item: interviewer }) => {
                const isSelected = formData.interviewers.includes(interviewer.id);
                const hasAvailability = formData.date && 
                  interviewer.availability?.[formData.date]?.length > 0;

                return (
                  <TouchableOpacity
                    style={[
                      styles.interviewerOption,
                      isSelected && styles.selectedInterviewerOption,
                      !hasAvailability && formData.date && styles.unavailableInterviewerOption,
                    ]}
                    onPress={() => handleInterviewerToggle(interviewer.id)}
                  >
                    <View style={styles.interviewerHeader}>
                      <View style={styles.interviewerAvatar}>
                        <Text style={styles.interviewerInitials}>
                          {interviewer.name.split(' ').map(n => n[0]).join('')}
                        </Text>
                      </View>
                      
                      <View style={styles.interviewerInfo}>
                        <Text style={[
                          styles.interviewerName,
                          isSelected && styles.selectedInterviewerName,
                        ]}>
                          {interviewer.name}
                        </Text>
                        <Text style={styles.interviewerTitle}>{interviewer.title}</Text>
                        <Text style={styles.interviewerDepartment}>{interviewer.department}</Text>
                        
                        <View style={styles.interviewerMeta}>
                          <View style={styles.ratingContainer}>
                            <MaterialCommunityIcons name="star" size={14} color="#F59E0B" />
                            <Text style={styles.ratingText}>{interviewer.rating}</Text>
                          </View>
                          <Text style={styles.interviewCountText}>
                            {interviewer.totalInterviews} interviews
                          </Text>
                        </View>
                      </View>
                      
                      <View style={styles.selectionIndicator}>
                        {isSelected ? (
                          <MaterialCommunityIcons name="checkbox-marked-circle" size={24} color="#10B981" />
                        ) : (
                          <MaterialCommunityIcons name="checkbox-blank-circle-outline" size={24} color="#CBD5E1" />
                        )}
                      </View>
                    </View>
                    
                    {formData.date && (
                      <View style={styles.availabilityIndicator}>
                        {hasAvailability ? (
                          <View style={styles.availableIndicator}>
                            <MaterialCommunityIcons name="check-circle" size={16} color="#10B981" />
                            <Text style={styles.availableText}>
                              Available ({interviewer.availability[formData.date].length} slots)
                            </Text>
                          </View>
                        ) : (
                          <View style={styles.unavailableIndicator}>
                            <MaterialCommunityIcons name="close-circle" size={16} color="#EF4444" />
                            <Text style={styles.unavailableText}>Not available</Text>
                          </View>
                        )}
                      </View>
                    )}
                    
                    {interviewer.expertise && (
                      <View style={styles.expertiseContainer}>
                        {interviewer.expertise.slice(0, 3).map((skill, index) => (
                          <View key={index} style={styles.expertiseChip}>
                            <Text style={styles.expertiseText}>{skill}</Text>
                          </View>
                        ))}
                        {interviewer.expertise.length > 3 && (
                          <Text style={styles.moreExpertise}>
                            +{interviewer.expertise.length - 3} more
                          </Text>
                        )}
                      </View>
                    )}
                  </TouchableOpacity>
                );
              }}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render interview type picker modal
  const renderTypePicker = () => {
    return (
      <Modal visible={showTypePicker} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Interview Type</Text>
              <TouchableOpacity onPress={() => setShowTypePicker(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            <FlatList
              data={interviewTypes}
              keyExtractor={(item) => item.id}
              style={styles.modalContent}
              renderItem={({ item: type }) => (
                <TouchableOpacity
                  style={[
                    styles.typeOption,
                    formData.interviewType === type.id && styles.selectedTypeOption,
                  ]}
                  onPress={() => {
                    handleInputChange('interviewType', type.id);
                    handleInputChange('duration', type.duration);
                    handleInputChange('preparationTime', type.preparationTime);
                    setShowTypePicker(false);
                  }}
                >
                  <View style={styles.typeHeader}>
                    <View style={[styles.typeColorIndicator, { backgroundColor: type.color }]} />
                    <View style={styles.typeInfo}>
                      <Text style={[
                        styles.typeName,
                        formData.interviewType === type.id && styles.selectedTypeName,
                      ]}>
                        {type.name}
                      </Text>
                      <Text style={styles.typeDescription}>{type.description}</Text>
                      <View style={styles.typeMeta}>
                        <Text style={styles.typeDuration}>{type.duration} minutes</Text>
                        <Text style={styles.typePrep}>
                          {type.preparationTime} min prep
                        </Text>
                      </View>
                    </View>
                    {formData.interviewType === type.id && (
                      <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
                    )}
                  </View>
                  
                  {type.stages && (
                    <View style={styles.stagesContainer}>
                      <Text style={styles.stagesTitle}>Interview Stages:</Text>
                      <View style={styles.stagesList}>
                        {type.stages.map((stage, index) => (
                          <View key={index} style={styles.stageItem}>
                            <Text style={styles.stageText}>{stage}</Text>
                          </View>
                        ))}
                      </View>
                    </View>
                  )}
                </TouchableOpacity>
              )}
            />
          </View>
        </View>
      </Modal>
    );
  };

  // Render conflict resolution modal
  const renderConflictModal = () => {
    const highSeverityConflicts = conflicts.filter(c => c.severity === 'high');
    
    return (
      <Modal visible={showConflictModal} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Scheduling Conflicts</Text>
              <TouchableOpacity onPress={() => setShowConflictModal(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <View style={styles.conflictContent}>
              <View style={styles.conflictWarning}>
                <MaterialCommunityIcons name="alert-circle" size={48} color="#EF4444" />
                <Text style={styles.conflictTitle}>Cannot Schedule Interview</Text>
                <Text style={styles.conflictSubtitle}>
                  The following conflicts prevent scheduling:
                </Text>
              </View>
              
              <View style={styles.conflictsList}>
                {highSeverityConflicts.map((conflict, index) => (
                  <View key={index} style={styles.conflictItem}>
                    <MaterialCommunityIcons name="close-circle" size={20} color="#EF4444" />
                    <Text style={styles.conflictMessage}>{conflict.message}</Text>
                  </View>
                ))}
              </View>
              
              <View style={styles.conflictActions}>
                <TouchableOpacity
                  style={styles.conflictActionButton}
                  onPress={() => {
                    setShowConflictModal(false);
                    setShowDatePicker(true);
                  }}
                >
                  <MaterialCommunityIcons name="calendar" size={20} color="#4A90E2" />
                  <Text style={styles.conflictActionText}>Choose Different Date</Text>
                </TouchableOpacity>
                
                <TouchableOpacity
                  style={styles.conflictActionButton}
                  onPress={() => {
                    setShowConflictModal(false);
                    setShowInterviewerPicker(true);
                  }}
                >
                  <MaterialCommunityIcons name="account-group" size={20} color="#4A90E2" />
                  <Text style={styles.conflictActionText}>Change Interviewers</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  // Get location type color
  const getLocationTypeColor = (type) => {
    switch (type) {
      case 'Physical': return '#10B981';
      case 'Virtual': return '#4A90E2';
      case 'Phone': return '#F59E0B';
      default: return '#6B7280';
    }
  };

  // Get selected location name
  const getSelectedLocationName = () => {
    const location = locations.find(l => l.id === formData.location);
    return location ? location.name : 'Select location';
  };

  // Get selected interviewer names
  const getSelectedInterviewerNames = () => {
    const selectedInterviewers = interviewers.filter(i => formData.interviewers.includes(i.id));
    if (selectedInterviewers.length === 0) return 'Select interviewers';
    if (selectedInterviewers.length === 1) return selectedInterviewers[0].name;
    if (selectedInterviewers.length === 2) {
      return `${selectedInterviewers[0].name} and ${selectedInterviewers[1].name}`;
    }
    return `${selectedInterviewers[0].name} and ${selectedInterviewers.length - 1} others`;
  };

  // Get selected interview type name
  const getSelectedInterviewTypeName = () => {
    const type = interviewTypes.find(t => t.id === formData.interviewType);
    return type ? type.name : 'Select interview type';
  };

  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size={36} color="#4A90E2" />
        <Text style={styles.loadingText}>Loading interview data...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity style={styles.backButton} onPress={handleCancel}>
            <Ionicons name="arrow-back" size={24} color="white" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Reschedule Interview</Text>
          <View style={styles.headerSpacer} />
        </View>
      </LinearGradient>

      <KeyboardAvoidingView
        style={styles.content}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          {/* Original Interview Info */}
          {originalInterview && (
            <View style={styles.originalInterviewCard}>
              <Text style={styles.originalInterviewTitle}>Current Interview</Text>
              <View style={styles.originalInterviewDetails}>
                <View style={styles.originalInterviewRow}>
                  <MaterialCommunityIcons name="calendar" size={16} color="#64748B" />
                  <Text style={styles.originalInterviewText}>
                    {new Date(originalInterview.interviewDate).toLocaleDateString('en-GB', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </Text>
                </View>
                <View style={styles.originalInterviewRow}>
                  <MaterialCommunityIcons name="clock" size={16} color="#64748B" />
                  <Text style={styles.originalInterviewText}>
                    {new Date(originalInterview.interviewDate).toLocaleTimeString('en-GB', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </View>
                <View style={styles.originalInterviewRow}>
                  <MaterialCommunityIcons name="map-marker" size={16} color="#64748B" />
                  <Text style={styles.originalInterviewText}>
                    {originalInterview.interviewLocation || originalInterview.location}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Conflicts Warning */}
          {conflicts.length > 0 && (
            <View style={styles.conflictsCard}>
              <View style={styles.conflictsHeader}>
                <MaterialCommunityIcons name="alert-circle" size={20} color="#F59E0B" />
                <Text style={styles.conflictsTitle}>Scheduling Conflicts</Text>
              </View>
              {conflicts.map((conflict, index) => (
                <View key={index} style={styles.conflictItemCard}>
                  <MaterialCommunityIcons 
                    name={conflict.severity === 'high' ? 'close-circle' : 'alert'} 
                    size={16} 
                    color={conflict.severity === 'high' ? '#EF4444' : '#F59E0B'} 
                  />
                  <Text style={styles.conflictText}>{conflict.message}</Text>
                </View>
              ))}
            </View>
          )}

          {/* Form Fields */}
          <View style={styles.formContainer}>
            {/* Date Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Date *</Text>
              <TouchableOpacity
                style={[styles.input, errors.date && styles.inputError]}
                onPress={() => setShowDatePicker(true)}
              >
                <MaterialCommunityIcons name="calendar" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.date && styles.placeholderText]}>
                  {formData.date 
                    ? new Date(formData.date).toLocaleDateString('en-GB', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    : 'Select date'
                  }
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.date && <Text style={styles.errorText}>{errors.date}</Text>}
            </View>

            {/* Time Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Time *</Text>
              <TouchableOpacity
                style={[styles.input, errors.time && styles.inputError]}
                onPress={() => setShowTimePicker(true)}
                disabled={!formData.date || availableTimeSlots.length === 0}
              >
                <MaterialCommunityIcons name="clock" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.time && styles.placeholderText]}>
                  {formData.time || 'Select time'}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.time && <Text style={styles.errorText}>{errors.time}</Text>}
              {formData.date && availableTimeSlots.length === 0 && (
                <Text style={styles.warningText}>
                  No available time slots for selected date and interviewers
                </Text>
              )}
            </View>

            {/* Location Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Location *</Text>
              <TouchableOpacity
                style={[styles.input, errors.location && styles.inputError]}
                onPress={() => setShowLocationPicker(true)}
              >
                <MaterialCommunityIcons name="map-marker" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.location && styles.placeholderText]}>
                  {getSelectedLocationName()}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.location && <Text style={styles.errorText}>{errors.location}</Text>}
            </View>

            {/* Interviewers Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interviewers *</Text>
              <TouchableOpacity
                style={[styles.input, errors.interviewers && styles.inputError]}
                onPress={() => setShowInterviewerPicker(true)}
              >
                <MaterialCommunityIcons name="account-group" size={20} color="#64748B" />
                <Text style={[styles.inputText, formData.interviewers.length === 0 && styles.placeholderText]}>
                  {getSelectedInterviewerNames()}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.interviewers && <Text style={styles.errorText}>{errors.interviewers}</Text>}
            </View>

            {/* Interview Type Selection */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Interview Type *</Text>
              <TouchableOpacity
                style={[styles.input, errors.interviewType && styles.inputError]}
                onPress={() => setShowTypePicker(true)}
              >
                <MaterialCommunityIcons name="clipboard-text" size={20} color="#64748B" />
                <Text style={[styles.inputText, !formData.interviewType && styles.placeholderText]}>
                  {getSelectedInterviewTypeName()}
                </Text>
                <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
              </TouchableOpacity>
              {errors.interviewType && <Text style={styles.errorText}>{errors.interviewType}</Text>}
            </View>

            {/* Duration */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Duration (minutes)</Text>
              <View style={styles.durationContainer}>
                <TouchableOpacity
                  style={styles.durationButton}
                  onPress={() => handleInputChange('duration', Math.max(15, formData.duration - 15))}
                >
                  <MaterialCommunityIcons name="minus" size={20} color="#64748B" />
                </TouchableOpacity>
                <Text style={styles.durationText}>{formData.duration}</Text>
                <TouchableOpacity
                  style={styles.durationButton}
                  onPress={() => handleInputChange('duration', Math.min(180, formData.duration + 15))}
                >
                  <MaterialCommunityIcons name="plus" size={20} color="#64748B" />
                </TouchableOpacity>
              </View>
            </View>

            {/* Reason for Rescheduling */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Reason for Rescheduling *</Text>
              <TextInput
                style={[styles.textArea, errors.reason && styles.inputError]}
                placeholder="Please provide a reason for rescheduling this interview..."
                placeholderTextColor="#94A3B8"
                value={formData.reason}
                onChangeText={(text) => handleInputChange('reason', text)}
                multiline
                numberOfLines={4}
                textAlignVertical="top"
              />
              {errors.reason && <Text style={styles.errorText}>{errors.reason}</Text>}
            </View>

            {/* Meeting Link (for virtual meetings) */}
            {formData.location && locations.find(l => l.id === formData.location)?.type === 'Virtual' && (
              <View style={styles.formGroup}>
                <Text style={styles.label}>Meeting Link</Text>
                <TextInput
                  style={styles.input}
                  placeholder="Meeting link will be auto-generated"
                  placeholderTextColor="#94A3B8"
                  value={formData.meetingLink}
                  onChangeText={(text) => handleInputChange('meetingLink', text)}
                  keyboardType="url"
                  autoCapitalize="none"
                />
              </View>
            )}

            {/* Additional Notes */}
            <View style={styles.formGroup}>
              <Text style={styles.label}>Additional Notes</Text>
              <TextInput
                style={styles.textArea}
                placeholder="Any additional notes or special requirements..."
                placeholderTextColor="#94A3B8"
                value={formData.notes}
                onChangeText={(text) => handleInputChange('notes', text)}
                multiline
                numberOfLines={3}
                textAlignVertical="top"
              />
            </View>

            {/* Notify Participants */}
            <View style={styles.formGroup}>
              <View style={styles.switchContainer}>
                <View style={styles.switchInfo}>
                  <Text style={styles.switchLabel}>Notify Participants</Text>
                  <Text style={styles.switchDescription}>
                    Send email notifications to all participants about the schedule change
                  </Text>
                </View>
                <Switch
                  value={formData.notifyParticipants}
                  onValueChange={(value) => handleInputChange('notifyParticipants', value)}
                  trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                  thumbColor={formData.notifyParticipants ? '#ffffff' : '#f4f3f4'}
                />
              </View>
            </View>
          </View>
        </ScrollView>

        {/* Action Buttons */}
        <View style={styles.actionContainer}>
          <TouchableOpacity
            style={styles.cancelButton}
            onPress={handleCancel}
            disabled={isSaving}
          >
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <ActivityIndicator size={20} color="#ffffff" />
            ) : (
              <>
                <MaterialCommunityIcons name="calendar-check" size={20} color="#ffffff" />
                <Text style={styles.saveButtonText}>Reschedule</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>

      {/* Modals */}
      {renderDatePicker()}
      {renderTimePicker()}
      {renderLocationPicker()}
      {renderInterviewerPicker()}
      {renderTypePicker()}
      {renderConflictModal()}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
  },
  header: {
    paddingTop: 10,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
    textAlign: 'center',
  },
  headerSpacer: {
    width: 40,
  },
  content: {
    flex: 1,
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -10,
  },
  scrollContainer: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 100,
  },
  originalInterviewCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  originalInterviewTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 12,
  },
  originalInterviewDetails: {
    gap: 8,
  },
  originalInterviewRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  originalInterviewText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
  },
  conflictsCard: {
    backgroundColor: '#fef3c7',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#fbbf24',
  },
  conflictsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  conflictsTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#92400e',
    marginLeft: 8,
  },
  conflictItemCard: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  conflictText: {
    fontSize: 14,
    color: '#92400e',
    marginLeft: 8,
    flex: 1,
  },
  formContainer: {
    gap: 20,
  },
  formGroup: {
    gap: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
  },
  input: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: 12,
  },
  inputError: {
    borderColor: '#ef4444',
    backgroundColor: '#fef2f2',
  },
  inputText: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
  },
  placeholderText: {
    color: '#94a3b8',
  },
  textArea: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    fontSize: 16,
    color: '#0f172a',
    minHeight: 100,
  },
  errorText: {
    fontSize: 14,
    color: '#ef4444',
    marginTop: 4,
  },
  warningText: {
    fontSize: 14,
    color: '#f59e0b',
    marginTop: 4,
  },
  durationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  durationButton: {
    padding: 12,
    borderRadius: 8,
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  durationText: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginHorizontal: 20,
    minWidth: 60,
    textAlign: 'center',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  switchInfo: {
    flex: 1,
    marginRight: 16,
  },
  switchLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 4,
  },
  switchDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  actionContainer: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 16,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#f8fafc',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#64748B',
  },
  saveButton: {
    flex: 2,
    backgroundColor: '#4A90E2',
    paddingVertical: 16,
    borderRadius: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
  },
  saveButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.8,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  modalOption: {
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
  },
  modalOptionText: {
    fontSize: 16,
    color: '#0f172a',
  },
  selectedOption: {
    backgroundColor: '#f0f8ff',
  },
  selectedOptionText: {
    color: '#4A90E2',
    fontWeight: '600',
  },
  weekendOption: {
    backgroundColor: '#fef3c7',
  },
  weekendText: {
    color: '#92400e',
  },
  dateOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  todayBadge: {
    backgroundColor: '#10b981',
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  todayBadgeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  timeSlot: {
    flex: 1,
    margin: 4,
    paddingVertical: 12,
    paddingHorizontal: 8,
    backgroundColor: '#f8fafc',
    borderRadius: 8,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedTimeSlot: {
    backgroundColor: '#4A90E2',
    borderColor: '#4A90E2',
  },
  timeSlotText: {
    fontSize: 14,
    color: '#0f172a',
    fontWeight: '600',
  },
  selectedTimeSlotText: {
    color: '#ffffff',
  },
  noAvailabilityContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  noAvailabilityTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#64748B',
    marginTop: 16,
    marginBottom: 8,
  },
  noAvailabilityText: {
    fontSize: 14,
    color: '#94a3b8',
    textAlign: 'center',
    lineHeight: 20,
  },
  locationOption: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedLocationOption: {
    borderColor: '#4A90E2',
    backgroundColor: '#f0f8ff',
  },
  locationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  locationInfo: {
    flex: 1,
  },
  locationName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  selectedLocationName: {
    color: '#4A90E2',
  },
  locationMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  locationTypeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 4,
  },
  locationTypeBadgeText: {
    fontSize: 12,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  locationCapacity: {
    fontSize: 12,
    color: '#64748B',
  },
  locationDetails: {
    marginTop: 8,
  },
  locationAddress: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  platformText: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  amenitiesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  amenityChip: {
    backgroundColor: '#e0f2fe',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  amenityText: {
    fontSize: 12,
    color: '#0891b2',
  },
  moreAmenities: {
    fontSize: 12,
    color: '#64748B',
    fontStyle: 'italic',
  },
  featuresContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  featureChip: {
    backgroundColor: '#dbeafe',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  featureText: {
    fontSize: 12,
    color: '#1d4ed8',
  },
  interviewerOption: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedInterviewerOption: {
    borderColor: '#10b981',
    backgroundColor: '#f0fdf4',
  },
  unavailableInterviewerOption: {
    opacity: 0.6,
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca',
  },
  interviewerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  interviewerAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  interviewerInitials: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  interviewerInfo: {
    flex: 1,
  },
  interviewerName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 2,
  },
  selectedInterviewerName: {
    color: '#10b981',
  },
  interviewerTitle: {
    fontSize: 14,
    color: '#4A90E2',
    marginBottom: 1,
  },
  interviewerDepartment: {
    fontSize: 12,
    color: '#64748B',
    marginBottom: 4,
  },
  interviewerMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  ratingText: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '600',
  },
  interviewCountText: {
    fontSize: 12,
    color: '#64748B',
  },
  selectionIndicator: {
    marginLeft: 8,
  },
  availabilityIndicator: {
    marginTop: 8,
    marginBottom: 4,
  },
  availableIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  availableText: {
    fontSize: 12,
    color: '#10b981',
    fontWeight: '600',
  },
  unavailableIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  unavailableText: {
    fontSize: 12,
    color: '#ef4444',
    fontWeight: '600',
  },
  expertiseContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 8,
  },
  expertiseChip: {
    backgroundColor: '#e0f2fe',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  expertiseText: {
    fontSize: 12,
    color: '#0891b2',
  },
  moreExpertise: {
    fontSize: 12,
    color: '#64748B',
    fontStyle: 'italic',
  },
  typeOption: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedTypeOption: {
    borderColor: '#4A90E2',
    backgroundColor: '#f0f8ff',
  },
  typeHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  typeColorIndicator: {
    width: 4,
    height: 40,
    borderRadius: 2,
    marginRight: 12,
  },
  typeInfo: {
    flex: 1,
  },
  typeName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  selectedTypeName: {
    color: '#4A90E2',
  },
  typeDescription: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  typeMeta: {
    flexDirection: 'row',
    gap: 16,
  },
  typeDuration: {
    fontSize: 12,
    color: '#10b981',
    fontWeight: '600',
  },
  typePrep: {
    fontSize: 12,
    color: '#f59e0b',
    fontWeight: '600',
  },
  stagesContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
  },
  stagesTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  stagesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 4,
  },
  stageItem: {
    backgroundColor: '#e0f2fe',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  stageText: {
    fontSize: 12,
    color: '#0891b2',
  },
  conflictContent: {
    padding: 20,
  },
  conflictWarning: {
    alignItems: 'center',
    marginBottom: 24,
  },
  conflictTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ef4444',
    marginTop: 12,
    marginBottom: 8,
  },
  conflictSubtitle: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
  },
  conflictsList: {
    marginBottom: 24,
  },
  conflictItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fef2f2',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  conflictMessage: {
    fontSize: 14,
    color: '#dc2626',
    marginLeft: 8,
    flex: 1,
  },
  conflictActions: {
    gap: 12,
  },
  conflictActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f8ff',
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#4A90E2',
    gap: 8,
  },
  conflictActionText: {
    fontSize: 16,
    color: '#4A90E2',
    fontWeight: '600',
  },
});

export default RescheduleInterviewScreen;
